<template>
    <OrdersTopBarCard
        ref="TopBar"
        v-model:search="table.search"
        :selected-order-uuids="selectedOrderUuids"
        :sort-orders="table.orders"
        :active-filters-count="table.activeFiltersCount"
        :filters="table.filters"
        :orders="table.orders"
        @fetch:orders="fetchOrders()"
        @update:filters="(filters, activeFiltersCount, fieldsState) => updateFilters(filters, activeFiltersCount, fieldsState)"
    />
    <div class="card">
        <div class="table-responsive">
            <table class="table table-fixed datatable-basic dataTable">
                <thead>
                    <tr>
                        <th width="50px">
                            <AppCheckbox
                                :model-value="selectedOrderUuids.length === orders.length"
                                @update:model-value="selectAllOrders()"
                            />
                        </th>
                        <th width="150px" :class="getSortingClass('id')" @click="toggleSort('id')">{{ $t('table.titles.id') }}</th>
                        <th width="150px">{{ $t('table.titles.status') }}</th>
                        <th width="200px">{{ $t('table.titles.client') }}</th>
                        <th width="200px">{{ $t('table.titles.source') }}</th>
                        <th width="150px">{{ $t('table.titles.payment') }}</th>
                        <th width="300px" :class="getSortingClass('delivery')" @click="toggleSort('delivery')">{{ $t('table.titles.delivery') }}</th>
                        <th width="150px">{{ $t('table.titles.sum') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="order in orders" :key="order.id">
                        <td>
                            <AppCheckbox
                                :model-value="selectedOrderUuids.includes(order.uuid)"
                                @update:model-value="selectOrder(order.uuid)"
                            />
                        </td>
                        <td>
                            <router-link
                                :to="{
                                    name: 'OrderProducts',
                                    params: {
                                        orderUuid: order.uuid
                                    }
                                }">
                                {{ order.id }}
                            </router-link>
                            <span class="text-muted text-smaller d-block long-string">{{ DateFormatter.formatDateTime(order.created_at) }}</span>
                        </td>
                        <td>
                            <AppNullableCell>
                                <template #default v-if="order.status">
                                    <span :class="`badge`" :style="{
                                        'color': 'white',
                                        'background-color': order.status.color
                                    }">{{ order.status.name }}</span>
                                </template>
                            </AppNullableCell>
                        </td>
                        <td>
                            <AppNullableCell>
                                <template #default v-if="order.client">
                                    <span class="long-string" :title="order.client.name">{{ order.client.name }}</span>
                                    <div>
                                        <div
                                            class="source-order-panel d-inline-flex"
                                            v-if="order.client.phone"
                                            v-click-outside="() => closePhoneMenu(order.uuid)"
                                            @click="togglePhoneMenu($event, order.uuid)"
                                        >
                                            <div>{{ formatPhone(order.client.phone) }}</div>
                                            <div
                                                v-if="openPhoneMenuUuid === order.uuid"
                                                class="dropdown-menu source-order-panel-menu"
                                                :style="phoneMenuStyle"
                                            >
                                                <a class="dropdown-item" @click.stop="copyPhone(order)">
                                                    <i class="icon-copy mr-2"></i>{{ $t('buttons.copy') }}
                                                </a>
                                                <AppDropDownMenuSubmenu>
                                                    <a class="dropdown-item" href="#" @click.stop.prevent>
                                                        <i class="icon-bubble mr-2"></i>{{ $t('labels.messengers') }}
                                                    </a>
                                                    <div class="dropdown-menu">
                                                        <a class="dropdown-item" :href="generateTelegramUrl(order.client.phone)" target="_blank">
                                                            <img src="/img/social-media/telegram.png" width="16" height="16" class="mr-2">Telegram
                                                        </a>
                                                        <a class="dropdown-item" :href="generateViberUrl(order.client.phone)" target="_blank">
                                                            <img src="/img/social-media/viber.png" width="16" height="16" class="mr-2">Viber
                                                        </a>
                                                        <a class="dropdown-item" :href="generateWhatsAppUrl(order.client.phone)" target="_blank">
                                                            <img src="/img/social-media/whatsapp.png" width="16" height="16" class="mr-2">WhatsApp
                                                        </a>
                                                    </div>
                                                </AppDropDownMenuSubmenu>
                                            </div>
                                        </div>
                                        <span v-else>{{ order.client.email }}</span>
                                    </div>
                                </template>
                            </AppNullableCell>
                        </td>
                        <td>
                            <AppNullableCell>
                                <template #default v-if="order.source">
                                    <div class="d-flex align-items-center">
                                        <img :src="order.source.image_file?.url || PLACEHOLDER_IMAGE_URL" class="source-image mr-2" />
                                        <div class="d-flex flex-column" style="overflow: hidden;">
                                            <span class="text-truncate text-body">{{ order.source.name }}</span>
                                            <div
                                                class="source-order-panel d-flex"
                                                v-if="order.external_order_number || order.integration_order"
                                                v-click-outside="() => closeSourceOrderMenu(order.uuid)"
                                                @click="toggleSourceOrderMenu($event, order.uuid)"
                                            >
                                                <div v-if="order.external_order_number" class="source-order-panel_number">{{ formatExternalOrderNumber(order) }}</div>
                                                <template v-if="order.integration_order">
                                                    <div class="source-order-panel_icon">
                                                        <i
                                                            class="icon-checkmark3"
                                                            :class="approveIconClass(order)"
                                                            :title="approveIconTitle(order)"
                                                        ></i>
                                                    </div>
                                                    <div class="source-order-panel_icon">
                                                        <i
                                                            class="icon-truck"
                                                            :class="order.integration_order.jobs?.set_delivery_number_on_market_place
                                                                ? 'text-orange source-order-panel_icon--blinking'
                                                                : {'text-orange': order.integration_order.delivery_number}"
                                                            :title="order.integration_order.jobs?.set_delivery_number_on_market_place ? $t('labels.set_ttn_in_progress') : null"
                                                        ></i>
                                                    </div>
                                                </template>
                                                <div
                                                    v-if="isResyncInProgress(order)"
                                                    class="source-order-panel_progress"
                                                    :title="$t('labels.resync_order_in_progress')"
                                                ></div>
                                                <div
                                                    v-if="openSourceOrderMenuUuid === order.uuid"
                                                    class="dropdown-menu source-order-panel-menu"
                                                    :style="sourceOrderMenuStyle"
                                                >
                                                    <a class="dropdown-item" @click.stop="copySourceOrderDeliveryNumber(order)">
                                                        <i class="icon-copy mr-2"></i>{{ $t('buttons.copy') }}
                                                    </a>
                                                    <AppDropDownMenuSubmenu>
                                                        <a
                                                            class="dropdown-item"
                                                            href="#"
                                                            :class="{ disabled: !order.integration_order, 'text-muted': !order.integration_order }"
                                                            @click.stop.prevent
                                                        >
                                                            <i class="icon-cart mr-2"></i>{{ $t('labels.marketplace_menu') }}
                                                        </a>
                                                        <div class="dropdown-menu" v-if="order.integration_order">
                                                            <a
                                                                class="dropdown-item"
                                                                :class="{ disabled: isResyncDisabled(order), 'text-muted': isResyncDisabled(order) }"
                                                                :title="resyncDisabledTitle(order)"
                                                                @click.stop="!isResyncDisabled(order) && resyncOrderFromMarketPlace(order)"
                                                            >
                                                                <i class="icon-sync mr-2"></i>{{ $t('buttons.update') }}
                                                            </a>
                                                            <a
                                                                class="dropdown-item"
                                                                :class="{ disabled: !generateSourceOrderUrl(order), 'text-muted': !generateSourceOrderUrl(order) }"
                                                                @click.stop="generateSourceOrderUrl(order) && openSourceOrderUrl(order)"
                                                            >
                                                                <i class="icon-link mr-2"></i>{{ $t('buttons.navigate') }}
                                                            </a>
                                                            <a
                                                                class="dropdown-item"
                                                                :class="{ disabled: isApproveDisabled(order), 'text-muted': isApproveDisabled(order) }"
                                                                :title="approveDisabledTitle(order)"
                                                                @click.stop="!isApproveDisabled(order) && approveOrderOnMarketPlace(order)"
                                                            >
                                                                <i class="icon-checkmark3 mr-2"></i>{{ $t('buttons.approve') }}
                                                            </a>
                                                            <a
                                                                class="dropdown-item"
                                                                :class="{ disabled: isSetTtnDisabled(order), 'text-muted': isSetTtnDisabled(order) }"
                                                                :title="setTtnDisabledTitle(order)"
                                                                @click.stop="!isSetTtnDisabled(order) && setDeliveryNumberOnMarketPlace(order)"
                                                            >
                                                                <i class="icon-truck mr-2"></i>{{ $t('buttons.set_ttn') }}
                                                            </a>
                                                        </div>
                                                    </AppDropDownMenuSubmenu>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </AppNullableCell>
                        </td>
                        <td>
                            <AppNullableCell>
                                <template #default v-if="order.payments && order.payments.length">
                                    {{ $t(`enums.OrderPaymentTypeEnum.${order.payments[0].type}`) }}<br>
                                    <span :class="['badge badge-pill', order.payments[0].is_paid ? 'badge-soft-success' : 'badge-soft-danger']">
                                        {{ order.payments[0].is_paid ? $t('labels.paid') : $t('labels.not_paid') }}
                                    </span>
                                </template>
                            </AppNullableCell>
                        </td>
                        <td>
                            <AppNullableCell>
                                <template #default v-if="order.delivery">
                                    <OrderDeliveryCell
                                        :delivery="order.delivery"
                                        :order="order"
                                        @created="order.delivery = $event"
                                    />
                                </template>
                            </AppNullableCell>
                        </td>
                        <td>
                            {{ Price.factory(order.sum).toCurrencyString("UAH") }}
                            <slot v-if="order.expenses_sum">
                                <br/><span class="text-danger">-{{ Price.factory(order.expenses_sum).toCurrencyString("UAH") }}</span>
                            </slot>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="card-footer d-flex justify-content-between align-items-center">
            <span>{{ $t('labels.showing') }} {{ start }} - {{ end }} {{ $t('labels.of') }} {{ table.total }} {{ $t('labels.entries') }}</span>
            <ul class="pagination pagination-flat align-self-center">
                <li :class="['page-item', 1 === table.page ? 'disabled' : '']" @click="changePage(table.page - 1)">
                    <a class="page-link legitRipple"><i class="icon-arrow-left12"></i></a>
                </li>

                <template v-for="pageNumber in pagesToRender" :key="pageNumber">
                    <li
                        :class="['page-item', pageNumber === table.page ? 'active' : '']"
                        @click="changePage(pageNumber)"
                    >
                        <a class="page-link legitRipple">{{ pageNumber }}</a>
                    </li>
                </template>

                <li :class="['page-item', table.page === pagesNumber ? 'disabled' : '']" @click="changePage(table.page + 1)">
                    <a class="page-link legitRipple"><i class="icon-arrow-right13"></i></a>
                </li>
            </ul>
            <InputGroup style="width: 200px;">
                <template #prepend>
                    <span class="input-group-text">{{ $t('labels.show') }}:</span>
                </template>
                <AppSelect
                    :model-value="table.limit"
                    :selected-value="table.limit"
                    @update:model-value="table.limit = $event.key"
                    :options="table.options"
                    optionKey="key"
                    optionValue="key"
                />
            </InputGroup>
        </div>
    </div>
</template>
<script>
import InputGroup from "../../../../../components/new/form/InputGroup.vue";
import AppNullableCell from "../../../../components/tables/AppNullableCell.vue";
import AppSelect from "../../../../../components/new/form/select/AppSelect.vue";
import OrderDeliveryCell from "./OrderDeliveryCell.vue";
import AppCheckbox from "../../../../../components/new/form/checkbox/AppCheckbox.vue";
import OrdersTopBarCard from "./OrdersTopBarCard.vue";
import AppDropDownMenuSubmenu from "../../../../components/dropdown/AppDropDownMenuSubmenu.vue";

import { privateChannel } from "../../../../services/echoService";
import { mapGetters } from "vuex";
import { httpClient } from "../../../../../services/HttpClient.ts";
import qs from "qs";
import { Mask } from "maska";
import { PLACEHOLDER_IMAGE_URL } from "../../../../constants";
import Price from "../../../../../services/Price/Price"
import { IntegrationServiceEnum } from "../../../../enums/IntegrationServiceEnum";
import DateFormatter from "../../../../../services/DateFormatter";
import vClickOutside from 'click-outside-vue3';
import MessageNotificator from "../../../../../services/MessageNotificator";

const SET_TTN_ERROR_TRANSLATION_KEYS = {
    'This order has no delivery, so there is no tracking number to send.': 'alerts.set_ttn_error_delivery_missing',
    'This delivery has no tracking number yet.': 'alerts.set_ttn_error_delivery_number_missing',
    'The marketplace already has a tracking number for this order.': 'alerts.set_ttn_error_delivery_number_already_set',
    'This marketplace does not accept tracking numbers.': 'alerts.set_ttn_error_service_unsupported',
    'This integration has no API credentials.': 'alerts.set_ttn_error_credentials_missing',
    'The API credentials of this integration are not active.': 'alerts.set_ttn_error_credentials_inactive',
    'The current order status on the marketplace does not allow sending a tracking number.': 'alerts.set_ttn_error_source_status_not_allowed',
    'The marketplace does not accept a tracking number for this delivery method.': 'alerts.set_ttn_error_delivery_method_unsupported',
};

const APPROVE_ORDER_ERROR_TRANSLATION_KEYS = {
    'This marketplace does not accept an order approve.': 'alerts.approve_order_error_service_unsupported',
    'This integration has no API credentials.': 'alerts.approve_order_error_credentials_missing',
    'The API credentials of this integration are not active.': 'alerts.approve_order_error_credentials_inactive',
    'The current order status on the marketplace does not allow an approve.': 'alerts.approve_order_error_source_status_not_allowed',
};

const RESYNC_ORDER_ERROR_TRANSLATION_KEYS = {
    'This marketplace does not accept an order resync.': 'alerts.resync_order_error_service_unsupported',
    'This integration has no API credentials.': 'alerts.resync_order_error_credentials_missing',
    'The API credentials of this integration are not active.': 'alerts.resync_order_error_credentials_inactive',
};

const STATUS_UPDATED_EVENT = '\\App\\Events\\Crm\\Delivery\\DeliveryStatusUpdatedEvent';
const NOTIFICATION_CREATED_EVENT = '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated';

const SET_TTN_JOB = 'set_delivery_number_on_market_place';
const APPROVE_ORDER_JOB = 'approve_order_on_market_place';
const RESYNC_ORDER_JOB = 'resync_order_from_market_place';

const MARKET_PLACE_JOB_BY_NOTIFICATION_TYPE = {
    'App\\Modules\\Integration\\Notifications\\SetDeliveryNumberOnMarketPlaceDispatchedNotification': {name: SET_TTN_JOB, isRunning: true},
    'App\\Modules\\Integration\\Notifications\\SetDeliveryNumberOnMarketPlaceSuccessNotification': {name: SET_TTN_JOB, isRunning: false},
    'App\\Modules\\Integration\\Notifications\\SetDeliveryNumberOnMarketPlaceFailedNotification': {name: SET_TTN_JOB, isRunning: false},
    'App\\Modules\\Integration\\Notifications\\ApproveOrderOnMarketPlaceDispatchedNotification': {name: APPROVE_ORDER_JOB, isRunning: true},
    'App\\Modules\\Integration\\Notifications\\ApproveOrderOnMarketPlaceSuccessNotification': {name: APPROVE_ORDER_JOB, isRunning: false},
    'App\\Modules\\Integration\\Notifications\\ApproveOrderOnMarketPlaceFailedNotification': {name: APPROVE_ORDER_JOB, isRunning: false},
    'App\\Modules\\Integration\\Notifications\\ResyncOrderFromMarketPlaceDispatchedNotification': {name: RESYNC_ORDER_JOB, isRunning: true},
    'App\\Modules\\Integration\\Notifications\\ResyncOrderFromMarketPlaceSuccessNotification': {name: RESYNC_ORDER_JOB, isRunning: false},
    'App\\Modules\\Integration\\Notifications\\ResyncOrderFromMarketPlaceFailedNotification': {name: RESYNC_ORDER_JOB, isRunning: false},
};

export default {
    name: "OrdersTablePage",
    components: {
        InputGroup, AppNullableCell, AppSelect, AppCheckbox, OrdersTopBarCard, OrderDeliveryCell,
        AppDropDownMenuSubmenu
    },
    directives: {
        clickOutside: vClickOutside.directive
    },
    data() {
        return {
            PLACEHOLDER_IMAGE_URL,
            Price,
            DateFormatter,
            table: {
                searchDebounceTimer: null,
                activeFiltersCount: 0,
                orders: [{
                    column: 'id',
                    dir: 'desc'
                }],
                page: 1,
                total: 0,
                limit: 10,
                options: [
                    {key: 10, value: 10},
                    {key: 25, value: 25},
                    {key: 50, value: 50},
                    {key: 100, value: 100},
                ],
                filters: {},
                search: ''
            },
            initializingFromQuery: false,
            fetchOrdersRequestId: 0,
            requestedJobKeys: new Set(),
            selectedOrderUuids: [],
            orders: [],
            openSourceOrderMenuUuid: null,
            sourceOrderMenuStyle: {},
            openPhoneMenuUuid: null,
            phoneMenuStyle: {},
            integrationServiceForOrderLinks: [
                IntegrationServiceEnum.SHOP_EXPRESS,
                IntegrationServiceEnum.WOOCOMMERCE,
                IntegrationServiceEnum.ROZETKA,
                IntegrationServiceEnum.EPICENTR,
                IntegrationServiceEnum.KASTA,
                IntegrationServiceEnum.PROM
            ]
        }
    },
    mounted() {
        this.sourceOrderMenuAnchor = null;
        this.phoneMenuAnchor = null;
        this.menuPositionsTracked = false;

        this.initializingFromQuery = true;
        this.initFromQuery();
        this.$nextTick(() => {
            this.initializingFromQuery = false;
        });
        this.subscribeToDeliveryEvents();
    },
    unmounted() {
        this.unsubscribeFromDeliveryEvents();
        this.stopTrackingMenuPositions();
    },
    methods: {
        isSourceOrderConfirmed(order) {
            return Boolean(order.integration_order?.was_approved);
        },
        formatExternalOrderNumber(order) {
            return this.truncateEpicentrId(order, order.external_order_number);
        },
        truncateEpicentrId(order, id) {
            if (order.source?.integration?.service === IntegrationServiceEnum.EPICENTR) {
                return id.split('-')[0];
            }

            return id;
        },
        copySourceOrderDeliveryNumber(order) {
            navigator.clipboard.writeText(order.integration_order?.source_id ?? order.external_order_number);
        },
        copyPhone(order) {
            navigator.clipboard.writeText(order.client.phone);
        },
        generateSourceOrderUrl(order) {
            if (!order.integration_order) {
                return null;
            }

            const deliveryNumber = order.integration_order.source_id;

            switch (order.source?.integration?.service) {
                case IntegrationServiceEnum.MAUDAU:
                    return `https://merchant.maudau.com.ua/orders/${deliveryNumber}/products`;
                case IntegrationServiceEnum.EVA:
                    return `https://seller.eva.ua/merchant/orders/${deliveryNumber}`;
                case IntegrationServiceEnum.EPICENTR:
                    return `https://admin.epicentrm.com.ua/oms/orders/${deliveryNumber}`;
                case IntegrationServiceEnum.PROM:
                    return `https://my.prom.ua/cms/order/edit/${deliveryNumber}`;
                case IntegrationServiceEnum.ROZETKA:
                    return `https://seller.rozetka.com.ua/main/orders/all?page=1&sort=-id&pageSize=20&id=${deliveryNumber}&types=1`;
                case IntegrationServiceEnum.KASTA:
                    return `https://hub.kasta.ua/customer-orders/new?order=${deliveryNumber}`;
                default:
                    return null;
            }
        },
        openSourceOrderUrl(order) {
            const url = this.generateSourceOrderUrl(order);

            if (url) {
                window.open(url, '_blank');
            }
        },
        jobKey(order, jobName) {
            return `${order.integration_order.uuid}:${jobName}`;
        },
        markJobDispatched(order, jobName) {
            if (this.requestedJobKeys.delete(this.jobKey(order, jobName))) {
                this.setJobRunning(order, jobName, true);
            }
        },
        setJobRunning(order, jobName, isRunning) {
            order.integration_order = {
                ...order.integration_order,
                jobs: {...order.integration_order.jobs, [jobName]: isRunning},
            };
        },
        async setDeliveryNumberOnMarketPlace(order) {
            this.requestedJobKeys.add(this.jobKey(order, SET_TTN_JOB));

            try {
                await httpClient.post(
                    `/projects/${this.$route.params.projectUuid}/crm/sources/${order.source.uuid}/orders/${order.integration_order.uuid}`,
                    undefined,
                    false,
                    { skipErrorNotification: true },
                );

                this.markJobDispatched(order, SET_TTN_JOB);

                new MessageNotificator().success(this.$t('alerts.set_delivery_number_on_market_place_dispatched'));
            } catch (e) {
                this.requestedJobKeys.delete(this.jobKey(order, SET_TTN_JOB));

                const translationKey = SET_TTN_ERROR_TRANSLATION_KEYS[e.response?.data?.message];

                new MessageNotificator().error(this.$t(translationKey ?? 'alerts.set_delivery_number_on_market_place_failed'));
            }
        },
        async approveOrderOnMarketPlace(order) {
            this.requestedJobKeys.add(this.jobKey(order, APPROVE_ORDER_JOB));

            try {
                await httpClient.post(
                    `/projects/${this.$route.params.projectUuid}/crm/sources/${order.source.uuid}/orders/${order.integration_order.uuid}/approve`,
                    undefined,
                    false,
                    { skipErrorNotification: true },
                );

                this.markJobDispatched(order, APPROVE_ORDER_JOB);

                new MessageNotificator().success(this.$t('alerts.approve_order_on_market_place_dispatched'));
            } catch (e) {
                this.requestedJobKeys.delete(this.jobKey(order, APPROVE_ORDER_JOB));

                const translationKey = APPROVE_ORDER_ERROR_TRANSLATION_KEYS[e.response?.data?.message];

                new MessageNotificator().error(this.$t(translationKey ?? 'alerts.approve_order_on_market_place_failed'));
            }
        },
        async resyncOrderFromMarketPlace(order) {
            this.requestedJobKeys.add(this.jobKey(order, RESYNC_ORDER_JOB));

            try {
                await httpClient.post(
                    `/projects/${this.$route.params.projectUuid}/crm/sources/${order.source.uuid}/orders/${order.integration_order.uuid}/resync`,
                    undefined,
                    false,
                    { skipErrorNotification: true },
                );

                this.markJobDispatched(order, RESYNC_ORDER_JOB);

                new MessageNotificator().success(this.$t('alerts.resync_order_from_market_place_dispatched'));
            } catch (e) {
                this.requestedJobKeys.delete(this.jobKey(order, RESYNC_ORDER_JOB));

                const translationKey = RESYNC_ORDER_ERROR_TRANSLATION_KEYS[e.response?.data?.message];

                new MessageNotificator().error(this.$t(translationKey ?? 'alerts.resync_order_from_market_place_failed'));
            }
        },
        isApproveInProgress(order) {
            return Boolean(order.integration_order?.jobs?.approve_order_on_market_place);
        },
        isSetTtnInProgress(order) {
            return Boolean(order.integration_order?.jobs?.set_delivery_number_on_market_place);
        },
        isResyncInProgress(order) {
            return Boolean(order.integration_order?.jobs?.resync_order_from_market_place);
        },
        isApproveDisabled(order) {
            return this.isSourceOrderConfirmed(order)
                || this.isApproveInProgress(order);
        },
        isSetTtnDisabled(order) {
            return this.isSetTtnInProgress(order);
        },
        isResyncDisabled(order) {
            return this.isResyncInProgress(order);
        },
        approveIconClass(order) {
            if (this.isApproveInProgress(order)) {
                return 'text-success source-order-panel_icon--blinking';
            }

            return this.isSourceOrderConfirmed(order) ? 'text-success' : 'text-muted';
        },
        approveIconTitle(order) {
            if (this.isApproveInProgress(order)) {
                return this.$t('labels.approve_order_in_progress');
            }

            return this.isSourceOrderConfirmed(order)
                ? this.$t('labels.source_order_confirmed')
                : this.$t('labels.source_order_not_confirmed');
        },
        approveDisabledTitle(order) {
            if (this.isApproveInProgress(order)) {
                return this.$t('labels.approve_order_in_progress');
            }

            if (this.isSourceOrderConfirmed(order)) {
                return this.$t('labels.source_order_confirmed');
            }

            return null;
        },
        setTtnDisabledTitle(order) {
            if (this.isSetTtnInProgress(order)) {
                return this.$t('labels.set_ttn_in_progress');
            }

            if (this.isApproveInProgress(order)) {
                return this.$t('labels.approve_order_in_progress');
            }

            return null;
        },
        resyncDisabledTitle(order) {
            return this.isResyncInProgress(order) ? this.$t('labels.resync_order_in_progress') : null;
        },
        buildMenuStyle(anchor) {
            const rect = anchor.getBoundingClientRect();

            return {
                position: 'fixed',
                top: `${rect.bottom}px`,
                left: `${rect.left}px`,
                zIndex: 1000
            };
        },
        updateMenuPositions() {
            if (this.sourceOrderMenuAnchor) {
                this.sourceOrderMenuStyle = this.buildMenuStyle(this.sourceOrderMenuAnchor);
            }

            if (this.phoneMenuAnchor) {
                this.phoneMenuStyle = this.buildMenuStyle(this.phoneMenuAnchor);
            }
        },
        trackMenuPositions() {
            if (this.menuPositionsTracked) {
                return;
            }

            window.addEventListener('scroll', this.updateMenuPositions, true);
            window.addEventListener('resize', this.updateMenuPositions);
            this.menuPositionsTracked = true;
        },
        stopTrackingMenuPositions() {
            if (!this.menuPositionsTracked) {
                return;
            }

            window.removeEventListener('scroll', this.updateMenuPositions, true);
            window.removeEventListener('resize', this.updateMenuPositions);
            this.menuPositionsTracked = false;
        },
        toggleSourceOrderMenu(event, orderUuid) {
            if (this.openSourceOrderMenuUuid === orderUuid) {
                this.closeSourceOrderMenu(orderUuid);
                return;
            }

            this.sourceOrderMenuAnchor = event.currentTarget;
            this.sourceOrderMenuStyle = this.buildMenuStyle(this.sourceOrderMenuAnchor);
            this.openSourceOrderMenuUuid = orderUuid;

            this.trackMenuPositions();
        },
        closeSourceOrderMenu(orderUuid) {
            if (this.openSourceOrderMenuUuid !== orderUuid) {
                return;
            }

            this.openSourceOrderMenuUuid = null;
            this.sourceOrderMenuAnchor = null;

            if (!this.openPhoneMenuUuid) {
                this.stopTrackingMenuPositions();
            }
        },
        togglePhoneMenu(event, orderUuid) {
            if (this.openPhoneMenuUuid === orderUuid) {
                this.closePhoneMenu(orderUuid);
                return;
            }

            this.phoneMenuAnchor = event.currentTarget;
            this.phoneMenuStyle = this.buildMenuStyle(this.phoneMenuAnchor);
            this.openPhoneMenuUuid = orderUuid;

            this.trackMenuPositions();
        },
        closePhoneMenu(orderUuid) {
            if (this.openPhoneMenuUuid !== orderUuid) {
                return;
            }

            this.openPhoneMenuUuid = null;
            this.phoneMenuAnchor = null;

            if (!this.openSourceOrderMenuUuid) {
                this.stopTrackingMenuPositions();
            }
        },
        getInternationalPhone(phone) {
            const localNumber = phone.replace(/\D/g, '').slice(-10);

            return `38${localNumber}`;
        },
        generateTelegramUrl(phone) {
            return `https://t.me/+${this.getInternationalPhone(phone)}`;
        },
        generateViberUrl(phone) {
            return `viber://chat?number=%2B${this.getInternationalPhone(phone)}`;
        },
        generateWhatsAppUrl(phone) {
            return `https://wa.me/${this.getInternationalPhone(phone)}`;
        },
        subscribeToDeliveryEvents() {
            this.channel = privateChannel(`projects.${this.project.id}`)
                .listen(STATUS_UPDATED_EVENT, this.onDeliveryStatusUpdated);

            this.subscribeToNotifications(this.profile?.id);
        },
        subscribeToNotifications(profileId) {
            if (!profileId || this.notificationChannel) {
                return;
            }

            this.notificationChannel = privateChannel(`projects.${this.project.id}.users.${profileId}`)
                .notification(this.onNotificationReceived);
        },
        unsubscribeFromDeliveryEvents() {
            if (this.channel) {
                this.channel.stopListening(STATUS_UPDATED_EVENT, this.onDeliveryStatusUpdated);

                this.channel = null;
            }

            if (this.notificationChannel) {
                this.notificationChannel.stopListening(NOTIFICATION_CREATED_EVENT, this.onNotificationReceived);

                this.notificationChannel = null;
            }
        },
        onDeliveryStatusUpdated(event) {
            const order = this.orders.find(order => order.delivery?.uuid === event.delivery_uuid);

            if (!order) {
                return;
            }

            order.delivery.status_label = event.status?.name || '';
            order.delivery.is_updating_status = false;
        },
        onNotificationReceived(notification) {
            const job = MARKET_PLACE_JOB_BY_NOTIFICATION_TYPE[notification.type];

            if (!job) {
                return;
            }

            const integrationOrder = notification.additional_data?.integration_order;
            const order = this.orders.find(order => order.integration_order?.uuid === integrationOrder?.uuid);

            if (!order || !integrationOrder) {
                return;
            }

            if (!job.isRunning) {
                this.requestedJobKeys.delete(this.jobKey(order, job.name));
            }

            // The payload is built outside of a user request, so only the job the notification is about is trusted.
            order.integration_order = {
                ...order.integration_order,
                ...integrationOrder,
                jobs: {
                    ...order.integration_order.jobs,
                    [job.name]: job.isRunning,
                },
            };
        },
        initFromQuery() {
            const query = qs.parse(window.location.search, { ignoreQueryPrefix: true, arrayLimit: 1000 });
            if (query.search) this.table.search = query.search;
            if (query.page) this.table.page = Number(query.page);
            if (query.limit) this.table.limit = Number(query.limit);
            if (query.orders) this.table.orders = query.orders;

            const filters = { ...query };
            delete filters.search;
            delete filters.page;
            delete filters.limit;
            delete filters.orders;

            if (Object.keys(filters).length > 0) {
                this.$refs.TopBar.restoreFilters(filters);
            }
        },
        updateQuery() {
            const params = {};
            if (this.table.search) params.search = this.table.search;
            if (this.table.page !== 1) params.page = this.table.page;
            if (this.table.limit !== 10) params.limit = this.table.limit;
            const defaultSort = JSON.stringify([{ column: 'id', dir: 'desc' }]);
            if (JSON.stringify(this.table.orders) !== defaultSort) params.orders = this.table.orders;
            Object.assign(params, this.table.filters);

            const queryString = qs.stringify(params);
            this.$router.replace(queryString ? `${this.$route.path}?${queryString}` : this.$route.path);
        },
        async fetchOrders() {
            const requestId = ++this.fetchOrdersRequestId;
            this.selectedOrderUuids = [];
            let request = {
                extra_fields: ["client", "delivery", "delivery.stickerFile", "delivery.waybillFile", "status", "source.imageFile", "payments", "delivery.stickerFile", "delivery.waybillFile", "integrationOrder", "integrationOrder.status", "integrationOrder.integration", "source.integration"],
                page: this.table.page,
                limit: this.table.limit,
                search: this.table.search,
                orders: this.table.orders,
                ...this.table.filters
            };

            await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/orders`, request)
            .then(response => {
                if (requestId !== this.fetchOrdersRequestId) {
                    return;
                }

                this.orders = response.data.data;
                this.table.total = response.data.meta.total;
            });
        },
        formatPhone(phone) {
            const mask = new Mask({ mask: '+38 (###) ###-##-##' });
            return mask.masked(phone);
        },
        changePage(newValue) {
            if (newValue > this.pagesNumber || newValue < 1 || newValue === this.table.page) {
                return;
            }

            this.selectedValues = [];
            this.table.page = newValue;

            this.updateQuery();
            this.fetchOrders();
        },
        updateFilters(filters, activeFiltersCount = 0) {
            this.table.activeFiltersCount = activeFiltersCount;

            let formattedFilters = {};

            for (const [key, value] of Object.entries(filters)) {
                if (typeof value === 'boolean') {
                    formattedFilters[key] = Number(value);
                    continue;
                }

                formattedFilters[key] = value;
            }

            this.table.filters = formattedFilters;

            this.updateQuery();
            this.fetchOrders();
        },
        getSortingClass(columnKey) {
            const order = this.table.orders.find(order => order.column === columnKey);

            if (!order) {
                return 'sorting';
            }

            return order.dir === 'asc' ? 'sorting_asc' : 'sorting_desc';
        },
        toggleSort(columnKey) {
            let orders = [...this.table.orders];
            let orderIndex = orders.findIndex(order => order.column === columnKey);

            if (orderIndex === -1) {
                orders.push({ column: columnKey, dir: 'asc' });
            } else {
                let order = orders[orderIndex];

                if (order.dir === 'asc') {
                    order.dir = 'desc';
                } else if (order.dir === 'desc') {
                    orders.splice(orderIndex, 1);
                } else {
                    order.dir = 'asc';
                }
            }

            this.table.orders = orders;
            this.updateQuery();
            this.fetchOrders();
        },
        selectOrder(orderUuid) {
            const index = this.selectedOrderUuids.indexOf(orderUuid);

            if (index === -1) {
                this.selectedOrderUuids.push(orderUuid);
            } else {
                this.selectedOrderUuids.splice(index, 1);
            }
        },
        selectAllOrders() {
            if (this.selectedOrderUuids.length === this.orders.length) {
                this.selectedOrderUuids = [];
            } else {
                this.selectedOrderUuids = this.orders.map(order => order.uuid);
            }
        },
    },
    computed: {
        ...mapGetters({
            project: "route/project",
            profile: "profile/profile"
        }),
        start() {
            if (this.table.page === 1) {
                return 1;
            }

            return ((this.table.page - 1) * this.table.limit) + 1
        },
        end() {
            let end = (this.start + this.table.limit) - 1;

            return end > this.table.total ? this.table.total : end;
        },
        pagesNumber() {
            if (this.table.total <= this.table.limit) {
                return 1;
            }

            return Math.ceil(this.table.total / this.table.limit);
        },
        pagesToRender() {
            const visible = 5;
            let start = this.table.page;
            let end = this.table.page + visible - 1;

            if (this.table.page > this.pagesNumber - visible) {
                start = this.pagesNumber - visible + 1;
                end = this.pagesNumber;
            }

            start = Math.max(start, 1);

            const pages = [];
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            return pages;
        },
    },
    watch: {
        'table.limit'() {
            this.updateQuery();
            this.fetchOrders();
        },
        'table.search'(newValue, oldValue) {
            if (this.initializingFromQuery) return;

            this.table.page = 1;

            if (this.table.searchDebounceTimer) {
                clearTimeout(this.table.searchDebounceTimer);
            }

            this.table.searchDebounceTimer = setTimeout(() => {
                this.updateQuery();
                this.fetchOrders();
                this.table.searchDebounceTimer = null;
            }, 500);
        },
        'profile.id'(id) {
            this.subscribeToNotifications(id);
        },
    }
}
</script>
<style scoped>
.source-order-panel {
    position: relative;
    border: 1px solid #eaeaea;
    border-radius: .375rem;
    padding: 0px 10px;
    gap: 10px;
    background: #f8f8f8;
}
.source-order-panel_number {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.source-order-panel:hover {
    background: #eeeeee;
    cursor: pointer;
}
.source-order-panel-menu {
    display: block;
}
.source-order-panel > .source-order-panel_icon {
    color: #999;
}
.source-order-panel > .source-order-panel_icon > i {
    font-size: 10px;
}
.source-order-panel_icon--blinking {
    animation: source-order-panel-icon-blink 1s ease-in-out infinite;
}
@keyframes source-order-panel-icon-blink {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.25;
    }
}
.source-order-panel_progress {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    overflow: hidden;
    border-radius: 0 0 .375rem .375rem;
}
.source-order-panel_progress::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 40%;
    background-color: #4caf50;
    animation: source-order-panel-progress 1.2s linear infinite;
}
@keyframes source-order-panel-progress {
    0% {
        left: -40%;
    }
    100% {
        left: 100%;
    }
}
.search-block {
    display: flex;
    gap: 10px;
}
.source-image {
    border-radius: 5px;
    object-fit: cover;
    object-position: center;
    width: 20px;
    height: 20px;
}
.text-smaller {
    font-size: 12px;
    line-height: 18px;
}
.long-string {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
}
</style>
